/**
 * Title and description for the home page.
 *
 * Lifted from the reference build's site-wide default (app/layout.tsx), which is
 * where its home page took them from. Set per-page here rather than as this
 * site's default: the default still describes the recruitment business, and the
 * pages that serve it — /for-qa-engineers, /for-employers, the application
 * forms — are unchanged and still want it.
 */
export const seo = {
  title: "Quality Engineering for Regulated Software — WeAreQA",
  description:
    "AI-driven validation, QA consulting and intelligent quality operations for teams shipping regulated software in FinTech, Healthcare and eCommerce.",
} as const;

/**
 * Homepage copy, verbatim from design/Homepage.dc.html. Client-approved.
 * Never rewrite, shorten or "improve" any string in this file.
 */

export const hero = {
  badge: "FinTech · Healthcare & Pharma · eCommerce",
  /** The H1 is split so "quality." can carry the yellow. */
  headingLead: "We architect ",
  headingAccent: "quality.",
  lead: "AI-driven validation, QA consulting and intelligent quality operations for teams shipping regulated software. We design the gates that decide what reaches production — and prove they hold.",
  primaryCta: { label: "Schedule an Engineering Call", href: "#book" },
  secondaryCta: { label: "Take the QA maturity check", href: "#assessment" },
} as const;

/**
 * The quality-gates panel. These figures depict a product UI — they are not
 * company metrics and are not an exception to the two-metric rule. Verbatim.
 */
export const gatesPanel = {
  release: "release / 2026.4.1",
  label: "QUALITY GATES",
  footerLeft: "3 of 5 gates passed · no blockers",
  footerRight: "promote to production",
} as const;

export type Gate =
  | {
      state: "passed";
      label: string;
      count: string;
      popDelay: string;
      barDuration: string;
      barDelay: string;
    }
  | { state: "active"; label: string; count: string }
  | { state: "queued"; label: string; count: string };

export const gates: Gate[] = [
  {
    state: "passed",
    label: "Unit & contract",
    count: "1,284",
    popDelay: ".4s",
    barDuration: ".5s",
    barDelay: "0s",
  },
  {
    state: "passed",
    label: "API & integration",
    count: "412",
    popDelay: "1s",
    barDuration: ".6s",
    barDelay: ".5s",
  },
  {
    state: "passed",
    label: "Regression · Playwright",
    count: "2,940",
    popDelay: "1.6s",
    barDuration: ".75s",
    barDelay: "1.1s",
  },
  { state: "active", label: "AI output validation", count: "live" },
  { state: "queued", label: "Compliance sign-off", count: "queued" },
];
